import React, { useEffect, useState, memo } from 'react';
import { Check, Clock, AlertCircle } from 'lucide-react';
import { scrollToElement } from '../utils/scrollToElement';
import { trackEvent } from '../utils/metaPixel';
const FINAL_PRICE = 6.36;
const COUNTDOWN_DURATION_MS = 20 * 60 * 1000; // 20 minutes
function handleCheckout() {
  trackEvent('AddToCart', {
    content_name: 'El Método del 1% - Bundle Completo',
    content_ids: ['53815682957678'],
    content_type: 'product',
    value: FINAL_PRICE,
    currency: 'USD'
  });
  window.location.assign('https://pay.hotmart.com/T106066532H?checkoutMode=10');
}
/**
 * Perpetual 10-minute countdown.  The timer ALWAYS starts fresh on every
 * page load (no localStorage persistence) and, instead of stopping at
 * 00:00, silently rolls back to 10:00 the moment it would hit zero — so
 * the visitor never sees an "expired" state that could break the offer's
 * urgency narrative.
 */
function OfferCountdown() {
  const [remaining, setRemaining] = useState<number>(COUNTDOWN_DURATION_MS);
  useEffect(() => {
    let deadline = Date.now() + COUNTDOWN_DURATION_MS;
    const tick = () => {
      let left = deadline - Date.now();
      // Never let the timer reach 00:00 — restart one tick before.
      if (left <= 1000) {
        deadline = Date.now() + COUNTDOWN_DURATION_MS;
        left = COUNTDOWN_DURATION_MS;
      }
      setRemaining(left);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);
  const totalSec = Math.floor(remaining / 1000);
  const mm = String(Math.floor(totalSec / 60)).padStart(2, '0');
  const ss = String(totalSec % 60).padStart(2, '0');
  // Low-time mode kicks in under 60s — bumps the visual urgency
  // (faster pulse, darker bordo, separator blink).
  const isUrgent = remaining < 60 * 1000 && remaining > 0;
  return <div className="flex flex-col items-stretch sm:items-start gap-2 mb-4 w-full sm:w-auto" data-id="element-80">
      <div role="timer" aria-live="polite" className={`inline-flex items-center gap-3 px-4 py-2.5 bg-bordo text-white shadow-soft-lg ${isUrgent ? 'animate-urgent-pulse' : 'animate-soft-pulse'}`} data-id="element-81">
        <Clock strokeWidth={2} className="w-4 h-4 md:w-[18px] md:h-[18px]" data-id="element-82" />
        <span className="uppercase tracking-[0.18em] text-[10px] md:text-[11px] font-medium" data-id="element-83">
          Termina en
        </span>
        <span className="font-serif tabular-nums text-2xl md:text-[28px] leading-none font-medium flex items-baseline" translate="no" data-id="element-84">
          <span data-id="element-85">{mm}</span>
          <span className={`mx-0.5 ${isUrgent ? 'animate-blink' : 'opacity-80'}`} aria-hidden="true" data-id="element-86">
            :
          </span>
          <span data-id="element-87">{ss}</span>
        </span>
      </div>

      {/* Limited-stock micro-line — sits flush under the timer and
                                stacks a second urgency cue (scarcity) on top of the time
                                pressure.  Number is intentionally specific to feel real. */}
      <div className="inline-flex items-center gap-2 px-1 text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-bordo font-medium" data-id="element-88">
        <AlertCircle strokeWidth={2} className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" data-id="element-89" />
        <span data-id="element-90">
          Solo{' '}
          <span className="tabular-nums font-semibold" translate="no" data-id="element-91">
            50
          </span>{' '}
          cupos disponibles a este precio
        </span>
      </div>
    </div>;
}
/**
 * Editorial 2-column hero (silent luxury, clean-girl biohacking).
 * Headline mirrors the ad creative for tight message match; right column
 * carries a large, cinematic photo of a serene woman in a sunlit kitchen —
 * the lifestyle the visitor is invited to step into.
 */
export function Hero() {
  return <section className="relative pt-24 md:pt-[60px] pb-12 md:pb-[60px] overflow-hidden bg-cream" data-id="element-92">
      <div className="max-w-6xl md:max-w-[780px] mx-auto px-6 w-full grid grid-cols-1 gap-10 items-center" data-id="element-93">
        {/* TEXT */}
        <div className="flex flex-col items-start text-left order-1" data-id="element-94">
          <span className="uppercase tracking-[0.22em] text-[11px] md:text-xs font-medium text-sand-dark mb-5" data-id="element-95">
            Para mujeres con SOP, hipotiroidismo o síntomas inexplicables
          </span>

          <h1 className="font-serif text-[2.5rem] sm:text-5xl md:text-[42px] leading-[1.05] text-ink mb-5 md:mb-6" data-id="element-96">
            Todo <em className="italic text-sand-dark font-normal" data-id="element-97">normal</em>.
            Pero sigues{' '}
            <em className="italic text-sand-dark font-normal" data-id="element-98">agotada</em>.
          </h1>

          <p className="font-serif text-lg md:text-[28px] text-ink-light italic mb-5 md:mb-6 leading-snug" data-id="element-99">
            El problema no está en tus exámenes.
          </p>

          <p className="text-base md:text-[16px] md:leading-[1.6] text-ink-light font-light leading-relaxed mb-6 md:mb-7 max-w-lg" data-id="element-100">
            <span className="text-ink font-medium" data-id="element-101">El Método del 1%</span> es un
            protocolo de raíz, diseñado para ese espacio entre{' '}
            <em className="italic" data-id="element-102">enferma</em> y{' '}
            <em className="italic" data-id="element-103">bien</em> — donde los análisis salen bien
            pero tú sigues sin sentirte tú misma. Recupera tu energía, balance y
            claridad de forma natural, sin dietas extremas, sin suplementos
            costosos y sin esperar otro diagnóstico.
          </p>

          <ul className="space-y-2 mb-8 md:mb-10" data-id="element-104">
            {['Para SOP e hipotiroidismo', 'Sin diagnóstico previo necesario', 'Protocolo de raíz, no parches temporales'].map(bullet => <li key={bullet} className="flex items-start gap-2.5 text-sm md:text-base text-ink-light font-light" data-id="element-105">
                <Check strokeWidth={2} className="w-4 h-4 mt-1 flex-shrink-0 text-sand-dark" data-id="element-106" />
                <span data-id="element-107">{bullet}</span>
              </li>)}
          </ul>

          {/* Eyebrow above CTA — anchors the perceived value vs the
           launch price right before the click. */}
          <p className="uppercase tracking-[0.18em] text-[11px] md:text-xs text-ink-light mb-3 animate-bordo-flash" data-id="element-108">
            Valor real:{' '}
            <span className="text-ink tabular-nums" translate="no" style={{
            textDecoration: 'line-through',
            textDecorationColor: '#B89665',
            textDecorationThickness: '2px'
          }} data-id="element-109">
              $57
            </span>{' '}
            — Lanzamiento:{' '}
            <span className="text-bordo font-medium" translate="no" data-id="element-110">
              ${FINAL_PRICE.toFixed(2)}
            </span>
          </p>

          {/* Persistent 5-min urgency timer.  Sits directly above the CTA
           so the time pressure registers right before the click. */}
          <OfferCountdown data-id="element-111" />

          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6 w-full sm:w-auto" data-id="element-112">
            <button type="button" onClick={handleCheckout} className="inline-flex items-center justify-center bg-sand hover:bg-sand-dark text-white px-8 py-4 text-sm font-medium tracking-widest uppercase transition-colors duration-300 active:scale-[0.98] w-full sm:w-auto" data-id="element-113">
              Quiero Cambiar Ahora — ${FINAL_PRICE.toFixed(2)}
            </button>
          </div>

          {/* Single hero testimonial — placed right under the CTA to give
           the visitor an immediate proof anchor before they keep
           scrolling.  Editorial pull-quote treatment matches the
           silent-luxury tone. */}
          <figure className="mt-6 md:mt-7 max-w-md border-l-2 border-bordo/40 pl-4 py-1" data-id="element-114">
            <div className="flex items-center gap-0.5 mb-2 text-bordo" aria-label="Calificación 5 de 5 estrellas" data-id="element-115">
              {[0, 1, 2, 3, 4].map(i => <svg key={i} viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true" data-id="element-116">
                  <path d="M10 1.5l2.6 5.3 5.9.85-4.25 4.14 1 5.85L10 14.77l-5.25 2.77 1-5.85L1.5 7.65l5.9-.85L10 1.5z" data-id="element-117" />
                </svg>)}
            </div>
            <blockquote className="font-serif italic text-[15px] md:text-base text-ink leading-snug" data-id="element-118">
              “Mis análisis siempre salían{' '}
              <em className="not-italic font-medium" data-id="element-119">‘perfectos’</em>, pero yo
              vivía agotada y con la cabeza nublada. A las tres semanas con el
              Método del 1% volví a reconocerme — duermo profundo, los antojos
              desaparecieron y bajé 4 kilos sin pesarme una sola vez.”
            </blockquote>
            <figcaption className="mt-2.5 text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-ink-light not-italic flex items-center flex-wrap gap-x-2 gap-y-1" data-id="element-120">
              <span className="font-medium text-ink" data-id="element-121">Camila R.</span>
              <span aria-hidden="true" data-id="element-122">·</span>
              <span data-id="element-123">43 años · Monterrey</span>
              <span aria-hidden="true" data-id="element-124">·</span>
              <span className="inline-flex items-center gap-1 text-sand-dark" data-id="element-125">
                <Check strokeWidth={2.5} className="w-3 h-3" data-id="element-126" />
                Compra verificada
              </span>
            </figcaption>
          </figure>

          {/* Bonus value stack — mirrors the line-item table inside the
           Bundle so the visitor sees what the offer contains right under
           the CTA, anchoring perceived value before they scroll. */}
          <div className="mt-7 md:mt-8 w-full max-w-md" data-id="element-127">
            <p className="uppercase tracking-[0.2em] text-[10px] md:text-xs text-bordo font-medium mb-3 md:mb-4 animate-bordo-flash" data-id="element-128">
              Incluye gratis hoy
            </p>
            <ul className="divide-y divide-ink/10 border-y border-ink/10" data-id="element-129">
              {[{
              label: 'Bono #1 · Protocolo SOS Antojos',
              price: '$9'
            }, {
              label: 'Bono #2 · Recetario Hormonal de 15 Min',
              price: '$7'
            }, {
              label: 'Bono #3 · Checklist de Supervivencia',
              price: '$4'
            }].map(bonus => <li key={bonus.label} className="py-2.5 md:py-3 flex items-center gap-3" data-id="element-130">
                  <span className="flex-1 text-sm md:text-[15px] text-ink font-light leading-snug min-w-0" data-id="element-131">
                    {bonus.label}
                  </span>
                  <span className="font-serif text-sm md:text-base text-ink-light tabular-nums flex-shrink-0 w-12 text-right" translate="no" style={{
                textDecoration: 'line-through',
                textDecorationColor: '#B89665',
                textDecorationThickness: '2px'
              }} data-id="element-132">
                    {bonus.price}
                  </span>
                  <span className="flex-shrink-0 w-16 text-center text-[10px] md:text-[11px] uppercase tracking-[0.16em] font-bold text-sand-dark bg-sand/15 px-2 py-0.5" data-id="element-133">
                    Gratis
                  </span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* CINEMATIC IMAGE — serene woman in a sunlit kitchen */}
        <div className="relative w-full order-2" data-id="element-134">
          <div className="relative" data-id="element-135">
            {/* Soft sage card behind, slightly rotated for an editorial feel */}
            <div className="absolute -inset-3 md:-inset-5 bg-sage/50 rounded-2xl rotate-[-2deg]" data-id="element-136"></div>
            <div className="relative aspect-[4/5] md:aspect-auto md:h-[600px] overflow-hidden rounded-xl shadow-soft-lg bg-white" data-id="element-137">
              <img src="https://cdn.magicpatterns.com/uploads/s3QwgX2TtZEFoHJr4H7BCk/foto_pagina_principal.png" sizes="(max-width: 640px) 90vw, (max-width: 1024px) 80vw, 540px" alt="Mujer meditando en un acantilado frente al mar al amanecer, bañada por los rayos del sol — el momento de paz tras reencontrarse con una misma" width={768} height={1365}
            // @ts-expect-error fetchpriority is valid HTML but typed loosely in React
            fetchpriority="high" loading="eager" decoding="async" className="w-full h-full md:max-h-[600px] object-cover object-center" data-id="element-138" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}